'use client';

import React from 'react';
import { useRouter } from 'next/navigation';

import { useForm as useFormspree, ValidationError } from '@formspree/react';
import { useForm } from 'react-hook-form';

import { HorizontalRule } from '~/components/common/horizontal-rule';
import { SectionHeader } from '~/components/common/section-header';
import { Button } from '~/components/ui/button';
import { Checkbox } from '~/components/ui/checkbox';
import { FancyRadioButton } from '~/components/ui/fancy-radio-button';
import { Input } from '~/components/ui/input';
import { Label } from '~/components/ui/label';
import { Textarea } from '~/components/ui/textarea';
import { FORMSPREE_FUNDRAISE_TO_SPONSOR_A_HOSPITAL, FORMSPREE_SPONSOR_A_HOSPITAL } from '~/lib/constants';
import { cn } from '~/lib/utils';
import { AlgoliaHospitalRecord } from '~/types/algolia';
import { formatCurrency } from '~/utils/number';

import { CitySearch, SelectedCity } from './city-search';
import { HospitalListing } from './hospital-listing';
import s from './hospital-sponsorship.module.css';
import { ProgramUnitSelect } from './program-unit-select';
import { USStatesSprite } from './united-states';

type FormValues = {
  mode: 'search' | 'need';
  searched_by: string;
  hospital: string;
  hospital_city: string;
  hospital_state: string;
  hospital_code: string;
  gaming_units: number;
  gaming_total: string;
  gaming_fully_sponsored: boolean;
  gown_units: number;
  gowns_total: string;
  gowns_fully_sponsored: boolean;
  toys_units: number;
  toys_total: string;
  toys_fully_sponsored: boolean;
  total: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  message: string;
  dedication: boolean;
  interested_in: string;
};

interface HospitalSponsorshipProps {
  type: 'Donate' | 'Fundraise';
}

const gameCost = 5000;
const gownCost = 40;
const toysCost = 400;

export function HospitalSponsorship({ type }: HospitalSponsorshipProps) {
  const router = useRouter();

  const programUnitSelectSectionEl = React.useRef<HTMLElement>(null);
  const [searchMethod, setSearchMethod] = React.useState<'city' | 'hospital'>('city');
  const [sponsorMethod, setSponsorMethod] = React.useState<'search' | 'need'>('search');
  const [hospital, setHospital] = React.useState<AlgoliaHospitalRecord | null>(null);
  const [suggestion, setSuggestion] = React.useState<SelectedCity | null>(null);
  const [totalDollars, setTotalDollars] = React.useState(0);

  const { setValue, register, handleSubmit, watch, resetField, formState } = useForm<FormValues>({
    defaultValues: {
      mode: sponsorMethod,
      searched_by: '',
      hospital: '',
      hospital_city: '',
      hospital_state: '',
      hospital_code: '',
      gaming_units: 0,
      gaming_total: formatCurrency(0),
      gaming_fully_sponsored: false,
      gown_units: 0,
      gowns_total: formatCurrency(0),
      gowns_fully_sponsored: false,
      toys_units: 0,
      toys_total: formatCurrency(0),
      toys_fully_sponsored: false,
      total: formatCurrency(0),
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      message: '',
      dedication: false,
      interested_in: '',
    },
  });
  const formspreeFormKey = type === 'Donate' ? FORMSPREE_SPONSOR_A_HOSPITAL : FORMSPREE_FUNDRAISE_TO_SPONSOR_A_HOSPITAL;
  const [formspreeState, sendToFormspree] = useFormspree(formspreeFormKey);

  const handleQuantyUpdate = React.useCallback(
    (op: string, amount: number) => {
      return op === 'add' ? setTotalDollars(totalDollars + amount) : setTotalDollars(totalDollars - amount);
    },
    [totalDollars],
  );

  const resetHospitalValues = React.useCallback(() => {
    setValue('hospital', '');
    setValue('hospital_city', '');
    setValue('hospital_state', '');
    setValue('gaming_fully_sponsored', false);
    setValue('gowns_fully_sponsored', false);
    setValue('toys_fully_sponsored', false);
  }, [setValue]);

  React.useEffect(() => {
    const locationInput = document.querySelector<HTMLElement>('.ap-input');
    const hospitalNameInput = document.querySelector<HTMLElement>('.ais-SearchBox-input');

    if (sponsorMethod === 'search' && locationInput !== null) {
      locationInput.focus();
    }

    if (sponsorMethod === 'search' && hospitalNameInput !== null) {
      hospitalNameInput.focus();
    }
  }, [sponsorMethod]);

  React.useEffect(() => {
    if (formspreeState.succeeded) {
      router.push(type === 'Donate' ? '/donate/hospitals/thank-you' : '/sponsors/hospitals/thank-you');
    }
  }, [formspreeState.succeeded, router, type]);

  return (
    <>
      <section className='mx-auto max-w-5xl px-6 sm:px-12'>
        <div className='grid gap-4 sm:grid-cols-2  md:gap-6'>
          <FancyRadioButton
            isActive={sponsorMethod === 'search'}
            onClick={() => {
              setSponsorMethod('search');
              setValue('mode', 'search');
              setValue('searched_by', 'Location');
            }}
            size='large'
            type='button'
          >
            Sponsor a specific hospital:
          </FancyRadioButton>
          <FancyRadioButton
            isActive={sponsorMethod === 'need'}
            onClick={() => {
              setSponsorMethod('need');
              setValue('mode', 'need');
              setValue('searched_by', '');
              resetHospitalValues();
            }}
            size='large'
            type='button'
          >
            Sponsor a medically underserved hospital of greatest need.
          </FancyRadioButton>
        </div>
      </section>
      <section className={cn('mx-auto w-full max-w-5xl px-6 sm:px-12', s.search)} hidden={sponsorMethod === 'need'}>
        <SectionHeader heading='Search for your hospital' />
        <div className='mx-auto mb-4 grid max-w-sm grid-cols-2 gap-4'>
          <FancyRadioButton
            isActive={searchMethod === 'city'}
            onClick={() => {
              setValue('searched_by', 'Location');
              setSearchMethod('city');
              setHospital(null);
              resetHospitalValues();
              setSuggestion(null);
            }}
            size='small'
            type='button'
          >
            Location
          </FancyRadioButton>
          <FancyRadioButton
            isActive={searchMethod === 'hospital'}
            onClick={() => {
              setValue('searched_by', 'Hospital name');
              setSearchMethod('hospital');
              setHospital(null);
              resetHospitalValues();
              setSuggestion(null);
            }}
            size='small'
            type='button'
          >
            Hospital Name
          </FancyRadioButton>
        </div>
        {searchMethod === 'city' && (
          <CitySearch
            onClear={() => {
              resetField('hospital');
              setHospital(null);
              setSuggestion(null);
            }}
            onSelectCity={(city) => setSuggestion(city)}
          />
        )}
        <HospitalListing
          onSelectHospital={(hit) => {
            setHospital(hit);
            setValue('hospital', hit?.name ?? '');
            setValue('hospital_city', hit?.city ?? '');
            setValue('hospital_state', hit?.state ?? '');
            setValue('hospital_code', hit?.code ?? '');
            if (hit === null) {
              resetHospitalValues();
            } else {
              programUnitSelectSectionEl.current?.scrollIntoView({ behavior: 'smooth' });
            }
          }}
          searchMethod={searchMethod}
          selectedCity={suggestion}
          selectedHospital={hospital}
        />
      </section>
      <section
        className='mx-auto max-w-3xl px-6 sm:px-12 md:mt-8 md:max-w-6xl lg:mb-8'
        ref={programUnitSelectSectionEl}
      >
        <SectionHeader heading={hospital ? `Support ${hospital.name}` : 'Select your Starlight Programs'} />
        <div className='grid gap-5 gap-y-12 sm:grid-cols-2 lg:grid-cols-3'>
          <ProgramUnitSelect
            cost={gameCost}
            hospitalNeed={hospital ? Number(hospital.needs.nintendoSwitch) : 0}
            icon='Gaming'
            image='/img/hospital-sponsorship/gaming.jpg'
            onQuantityUpdate={handleQuantyUpdate}
            onUnitAmountChange={(unitTotal: number, fullySponsored: boolean) => {
              setValue('gaming_units', unitTotal);
              setValue('gaming_total', formatCurrency(unitTotal * gameCost));
              setValue('gaming_fully_sponsored', fullySponsored);
            }}
            step={1}
            title='Starlight Nintendo Switch'
            unitDescription={type === 'Donate' ? 'donation' : 'to sponsor'}
          />
          <ProgramUnitSelect
            cost={gownCost}
            hospitalNeed={hospital ? Number(hospital.needs.gowns) : 0}
            icon='Gown'
            image='/img/hospital-sponsorship/gown.jpg'
            onQuantityUpdate={handleQuantyUpdate}
            onUnitAmountChange={(unitTotal: number, fullySponsored: boolean) => {
              setValue('gown_units', unitTotal);
              setValue('gowns_total', formatCurrency(unitTotal * gownCost));
              setValue('gowns_fully_sponsored', fullySponsored);
            }}
            step={50}
            title='Starlight Gowns'
            unitDescription='per gown'
          />
          <ProgramUnitSelect
            cost={toysCost}
            hospitalNeed={hospital ? Number(hospital.needs.toys) : 0}
            icon='Play'
            image='/img/hospital-sponsorship/toys.jpg'
            onQuantityUpdate={handleQuantyUpdate}
            onUnitAmountChange={(unitTotal: number, fullySponsored: boolean) => {
              setValue('toys_units', unitTotal);
              setValue('toys_total', formatCurrency(unitTotal * toysCost));
              setValue('toys_fully_sponsored', fullySponsored);
            }}
            step={1}
            title='Starlight Bundle of 75 Toy Sets'
            unitDescription='per bundle'
          />
        </div>
      </section>
      <section className='mx-auto flex max-w-2xl flex-col items-center px-6'>
        <HorizontalRule className='text-asphalt-10' />
        <h3 className='display-base text-foreground mx-0 mt-3 text-center md:mt-8 md:text-5xl lg:mt-10'>
          <small className='block text-xs md:text-lg lg:mb-2 lg:text-2xl'>
            {type === 'Donate' ? 'Total donation' : 'Total sponsorship'}:
          </small>
        </h3>
        <div className='display-base text-foreground mx-0 mb-8 text-center md:mb-8 md:text-5xl lg:mb-10'>
          {formatCurrency(totalDollars)}
        </div>
        <p className='mb-12 text-center text-base md:text-xl'>
          {type === 'Donate'
            ? 'Include your name to be recognized as the official sponsor. Make your pledge and we’ll contact you to process the donation.'
            : "Please provide your information and we'll contact you within one business day to get started."}
        </p>
        <form className='grid gap-8' onSubmit={handleSubmit(sendToFormspree)}>
          <div className='grid grid-cols-2 gap-8'>
            <Input
              aria-label='First Name'
              placeholder='First name'
              required
              type='text'
              {...register('first_name', { required: true })}
            />
            <ValidationError errors={formspreeState.errors} field='first_name' prefix='First Name' />
            <Input
              aria-label='Last Name'
              placeholder='Last name'
              required
              type='text'
              {...register('last_name', { required: true })}
            />
            <ValidationError errors={formspreeState.errors} field='last_name' prefix='Last Name' />
            <Input
              aria-label='Email'
              placeholder='Email address'
              required
              type='text'
              {...register('email', {
                required: true,
                pattern: {
                  value: /^[a-zA-Z0-9.!#$%&’*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/,
                  message: 'Invalid email address',
                },
              })}
            />
            <ValidationError errors={formspreeState.errors} field='email' prefix='Email address' />
            <Input
              aria-label='Phone'
              placeholder='Phone number'
              required
              type='text'
              {...register('phone', { required: true })}
            />
            <ValidationError errors={formspreeState.errors} field='phone' prefix='Phone number' />
          </div>
          <legend className='title-sm'>I am interested in sponsoring through:</legend>
          <div className='flex flex-wrap gap-4'>
            <div>
              <input className='mr-2' id='donation' {...register('interested_in')} type='radio' value='donation' />
              <Label htmlFor='donation'>Making a donation</Label>
            </div>
            <div>
              <input className='mr-2' {...register('interested_in')} id='fundraiser' type='radio' value='fundraiser' />
              <Label htmlFor='fundraiser'>Setting up a fundraiser</Label>
            </div>
          </div>
          <Checkbox
            {...register('dedication')}
            label='My Starlight program sponsorship is in honor or memory of a loved one.'
          />
          {watch('dedication') && (
            <Textarea id='message' placeholder='Tell us about your dedication.' {...register('message')} />
          )}
          <Button
            disabled={totalDollars === 0 || formspreeState.submitting}
            onClick={() => setValue('total', formatCurrency(totalDollars))}
            type='submit'
          >
            Pledge {formatCurrency(totalDollars)}
          </Button>
        </form>
      </section>
      <USStatesSprite />
    </>
  );
}
